import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Hooks } from "@xt/xtbase";
import step5Img from "@/assets/img/cfd-guide/guide1.png";
import lineImg from "@/assets/img/cfd-guide/line.png";
import Storage from "utils/storage";
import { useCfdGuideStageRect } from "./useCfdGuideStageRect";
import styles from "./StandardGuideStep5.module.scss";

const { useTranslation } = Hooks;

export type StandardGuideStep5Props = {
  onSkip: () => void;
  onNext: () => void;
};

const DESIGN_WIDTH = 375;

function getScale(): number {
  if (typeof window === "undefined") return 1;
  const w = window.innerWidth || DESIGN_WIDTH;
  return w / DESIGN_WIDTH;
}

const StandardGuideStep5: React.FC<StandardGuideStep5Props> = ({ onSkip, onNext }) => {
  const t = useTranslation();

  const scale = useMemo(() => getScale(), []);

  const imgSrc = (step5Img as any)?.src || (step5Img as any);
  const lineImgSrc = (lineImg as any)?.src || (lineImg as any);

  const anchor = useCfdGuideStageRect('[data-guide-target="actionButtons"]');

  const [mockLabel, setMockLabel] = useState(() => t("cfd.standardActionBuyLong"));
  const [tradeSide, setTradeSide] = useState<"buy" | "sell">("buy");

  const klineColorScheme = useMemo(() => {
    if (typeof window === "undefined") return "green_up" as const;
    return (Storage.get("klineColorScheme") as "green_up" | "red_up") || "green_up";
  }, []);

  const actionBtnClass = useMemo(() => {
    if (tradeSide === "buy") {
      return klineColorScheme === "green_up" ? styles.buyBtn : styles.sellBtn;
    }
    return klineColorScheme === "green_up" ? styles.sellBtn : styles.buyBtn;
  }, [tradeSide, klineColorScheme]);

  useEffect(() => {
    const btn = document.querySelector(
      '[data-guide-target="actionButtons"] button'
    ) as HTMLButtonElement | null;
    if (!btn) return;
    const txt = btn.textContent?.trim();
    if (txt) setMockLabel(txt);
    const ds = btn.getAttribute("data-trade-side");
    if (ds === "buy" || ds === "sell") setTradeSide(ds);
  }, [anchor]);

  return (
    <div style={{ ["--sg-scale" as any]: scale }}>
      <div
        className={styles.highlightAndTipWrapper}
        style={
          anchor
            ? {
                position: "fixed",
                top: anchor.top,
                left: anchor.left,
                width: anchor.width,
                zIndex: 10001,
              }
            : { visibility: "hidden" as const }
        }
      >
        <div className={styles.highlightRect} style={anchor ? { minHeight: anchor.height } : undefined}>
          <div className={styles.actionButtons}>
            <button type="button" className={`${styles.actionBtn} ${actionBtnClass}`} tabIndex={-1}>
              {mockLabel}
            </button>
          </div>
        </div>
        <div className={styles.tip}>
        <div className={styles.tipBg}>
          <div className={styles.tipText}>
            <div className={styles.tipTitle}>{t("cfd.guide.standard.step5.title")}</div>
            <div className={styles.tipDesc}>{t("cfd.guide.standard.step5.desc")}</div>
          </div>
          <img className={styles.tipImg} src={imgSrc} alt="" />
        </div>
        <div className={styles.star1} />
        <div className={styles.star2} />
        <img className={styles.lineImg} src={lineImgSrc} alt="" />
      </div>
      </div>

      {/* 底部按钮 */}
      <div className={styles.btnGroup}>
        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onNext}>
          {`${t("cfd.guide.next")} 5/7`}
        </button>
        <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onSkip}>
          {t("cfd.guide.skip")}
        </button>
      </div>
    </div>
  );
};

export default observer(StandardGuideStep5);
